import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess, HelpOutline } from '@mui/icons-material';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useAuth } from '@/lib';
import OnboardingStepWrapper from '../OnboardingStepWrapper';
import { useOnboarding } from '../../../context/OnboardingContext';
import { BusinessIdea, MonetizationStrategy, BusinessStage } from '../onboarding.types';
import { RocketShip } from '../../RocketShip';
import { shouldUseMockData } from '../../../config/dev';
import { mockEnhanceBusinessIdea } from '../../../services/mockAIService';

// Type-safe mapping objects for select options
const MONETIZATION_OPTIONS: { value: MonetizationStrategy; label: string; tooltip: string }[] = [
  {
    value: 'subscription',
    label: 'Subscription (SaaS)',
    tooltip: 'Customers pay a recurring fee (monthly/yearly) to access your service',
  },
  {
    value: 'one-time payment',
    label: 'One-time Payment',
    tooltip: 'Customers pay once to purchase your product or service',
  },
  {
    value: 'transaction fees',
    label: 'Transaction Fees',
    tooltip: 'You take a percentage or fixed fee from each transaction between users',
  },
  {
    value: 'ads or sponsorships',
    label: 'Ads or Sponsorships',
    tooltip: 'Revenue from displaying advertisements or sponsored content',
  },
  {
    value: 'affiliate commissions',
    label: 'Affiliate Commissions',
    tooltip: 'Earn money by referring customers to other products or services',
  },
  {
    value: 'donations or tipping',
    label: 'Donations or Tipping',
    tooltip: 'Voluntary payments from users who want to support your work',
  },
  {
    value: 'licensing',
    label: 'Licensing',
    tooltip: 'Sell the rights to use your intellectual property or technology to others',
  },
  {
    value: 'usage-based',
    label: 'Usage-based',
    tooltip: 'Customers pay based on how much they use your service (pay-as-you-go)',
  },
  {
    value: 'freemium + upsell',
    label: 'Freemium + Upsell',
    tooltip: 'Offer a free basic version and charge for premium features',
  },
];

const STAGE_OPTIONS: { value: BusinessStage; label: string; tooltip: string }[] = [
  {
    value: 'idea',
    label: 'Just an idea',
    tooltip: "You have a concept but haven't started any development or research yet",
  },
  {
    value: 'validation',
    label: 'Market validation',
    tooltip:
      "You're testing if people actually want this solution through surveys, interviews, or prototypes",
  },
  {
    value: 'mvp',
    label: 'MVP in development',
    tooltip: 'Building a minimal version of your product to test with real users',
  },
  {
    value: 'launched',
    label: 'Launched (pre-revenue)',
    tooltip:
      'Your product is live and available to users, but not yet generating significant revenue',
  },
  {
    value: 'scaling',
    label: 'Scaling',
    tooltip: "Your product is successful and you're growing your user base and revenue",
  },
];

// Partial BusinessIdea for the form (we'll only collect some fields)
type BusinessIdeaFormData = Partial<
  Pick<
    BusinessIdea,
    | 'summary'
    | 'description'
    | 'problemStatement'
    | 'solutionOverview'
    | 'targetAudience'
    | 'stage'
    | 'businessModel'
    | 'monetization'
    | 'differentiator'
  >
>;

// Character limits for form fields
const CHAR_LIMITS = {
  description: 300,
  problemStatement: 100,
  targetAudience: 50,
  differentiator: 50,
} as const;

// Helper function to get character counter color
const getCharCounterColor = (current: number, limit: number) => {
  const percentage = (current / limit) * 100;
  if (percentage >= 100) return 'text-red-500';
  if (percentage >= 80) return 'text-yellow-500';
  return 'text-text-secondary';
};

const UserBusinessIdea: React.FC = () => {
  const { state, setData, setLoading, setError, setStep } = useOnboarding();
  const auth = useAuth();
  const [formData, setFormData] = useState<BusinessIdeaFormData>({
    summary: '',
    description: '',
    problemStatement: '',
    solutionOverview: '',
    targetAudience: '',
    stage: 'idea',
    businessModel: 'saas',
    monetization: 'subscription',
    differentiator: '',
  });
  const [optionalFieldsExpanded, setOptionalFieldsExpanded] = useState(false);

  // Load saved data on component mount
  useEffect(() => {
    // First try to load from onboarding context
    if (state.data.businessIdea) {
      setFormData(state.data.businessIdea);
      return;
    }

    // Then try to load from localStorage
    const savedData = localStorage.getItem('userBusinessIdea');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error parsing saved business idea data:', error);
      }
    }
  }, [state.data.businessIdea]);

  // Save to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem('userBusinessIdea', JSON.stringify(formData));
  }, [formData]);

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('userBusinessIdeaExpanded', JSON.stringify(optionalFieldsExpanded));
  }, [optionalFieldsExpanded]);

  // Load expanded state from localStorage
  useEffect(() => {
    const savedExpanded = localStorage.getItem('userBusinessIdeaExpanded');
    if (savedExpanded) {
      try {
        const parsedExpanded = JSON.parse(savedExpanded);
        setOptionalFieldsExpanded(parsedExpanded);
      } catch (error) {
        console.error('Error parsing saved expanded state:', error);
      }
    }
  }, []);

  const handleInputChange = (field: keyof BusinessIdeaFormData, value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleContinue = async () => {
    if (!formData.description?.trim()) {
      // Could add validation here
      return;
    }

    // Set loading state and move to processing step
    setLoading(true);
    setData({ processingType: 'enhancing-business-idea' });
    setStep('processing');

    try {
      // Create OpenAI request to enhance the business idea
      const enhancedBusinessIdea = await enhanceBusinessIdeaWithAI(formData);

      const payload = {
        businessIdea: enhancedBusinessIdea,
      };

      setData(payload);

      // Clear localStorage after successful submission
      localStorage.removeItem('userBusinessIdea');
      localStorage.removeItem('userBusinessIdeaExpanded');

      // Move to review step
      setStep('business-idea-review');
    } catch (error) {
      console.error('Error enhancing business idea:', error);
      setError('Failed to enhance your business idea. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const enhanceBusinessIdeaWithAI = async (
    userInput: BusinessIdeaFormData,
  ): Promise<BusinessIdea> => {
    if (shouldUseMockData()) {
      // Use mock data in development
      console.log('🤖 Using mock AI service for development');
      const result = await mockEnhanceBusinessIdea({
        userInput: userInput.description,
        isMultipleIdeas: false,
      });

      if (result.data.success && result.data.businessIdea) {
        return result.data.businessIdea;
      } else {
        throw new Error(result.data.error || 'Mock AI service failed');
      }
    }

    // Use real OpenAI API in production
    const app = auth.getApp();
    const functions = getFunctions(app);
    const enhanceBusinessIdea = httpsCallable(functions, 'enhanceBusinessIdea');

    const result = await enhanceBusinessIdea({
      userInput: userInput.description,
      isMultipleIdeas: false,
    });

    const data = result.data as any;

    console.log('Raw response data:', data);

    // Handle the response structure from Firebase Functions
    // The response might be wrapped in a 'result' object
    const responseData = data.result || data;

    console.log('Processed response data:', responseData);

    if (responseData.success && responseData.businessIdea) {
      console.log('Successfully extracted business idea:', responseData.businessIdea);
      return responseData.businessIdea;
    } else {
      console.error('Response validation failed:', {
        success: responseData.success,
        businessIdea: responseData.businessIdea,
        error: responseData.error,
      });
      throw new Error(responseData.error || 'Failed to enhance business idea');
    }
  };

  const isDescriptionValid = (formData.description?.trim() || '').length > 0;

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      <RocketShip />
      <OnboardingStepWrapper
        title="Tell me about it!"
        subtitle="Describe your concept so I can help you validate and refine it"
        showBackButton={true}
      >
        <Box className="space-y-8 max-w-2xl mx-auto">
          {/* Main Business Description */}
          <Box>
            <Typography variant="h6" className="text-white mb-2">
              Business Description *
            </Typography>
            <Typography variant="body2" className="text-gray-300 pb-2">
              Describe your business idea in detail. Provide as much detail as you can
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="E.g. I'm building a mobile app that helps small restaurants manage their inventory in real-time using AI-powered demand forecasting. "
              variant="outlined"
              className="bg-gray-800"
              inputProps={{
                maxLength: CHAR_LIMITS.description,
              }}
              InputProps={{
                className: 'text-white',
                style: { color: 'white' },
              }}
              InputLabelProps={{
                style: { color: 'white' },
              }}
            />
            <Box className="flex justify-end mt-1">
              <Typography
                variant="caption"
                className={getCharCounterColor(
                  formData.description?.length || 0,
                  CHAR_LIMITS.description,
                )}
              >
                {formData.description?.length || 0}/{CHAR_LIMITS.description}
              </Typography>
            </Box>
          </Box>

          {/* Optional Fields Section */}
          <Box className="bg-app-surface rounded-lg p-6">
            <Box
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
            >
              <Box className="flex flex-col gap-1">
                <Box className="flex items-center gap-2">
                  <Typography variant="h6" className="text-white">
                    Additional Details
                  </Typography>
                  <Chip label="Optional" size="small" className="bg-blue-600 text-white text-xs" />
                </Box>
                <Typography variant="body2" sx={{ color: '#9D9FBE' }} className="text-sm">
                  Fill in as many of these fields as you can for better guidance
                </Typography>
              </Box>
              <IconButton size="medium" className="text-gray-400 w-10 h-10 min-w-10 min-h-10">
                {optionalFieldsExpanded ? (
                  <ExpandLess fontSize="large" />
                ) : (
                  <ExpandMore fontSize="large" />
                )}
              </IconButton>
            </Box>

            <Collapse in={optionalFieldsExpanded}>
              <Box className="pt-4">
                {/* Problem Statement */}
                <Box className="mt-6 mb-6">
                  <Typography variant="subtitle1" className="text-white mb-2">
                    What problem are you solving?
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.problemStatement || ''}
                    onChange={(e) => handleInputChange('problemStatement', e.target.value)}
                    placeholder="e.g., 'Restaurants lose 10-15% of revenue due to poor inventory management'"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                    className="bg-gray-800"
                    inputProps={{
                      maxLength: CHAR_LIMITS.problemStatement,
                    }}
                    InputProps={{
                      className: 'text-white',
                      style: { color: 'white' },
                    }}
                  />
                  <Box className="flex justify-end mt-1">
                    <Typography
                      variant="caption"
                      className={getCharCounterColor(
                        formData.problemStatement?.length || 0,
                        CHAR_LIMITS.problemStatement,
                      )}
                    >
                      {formData.problemStatement?.length || 0}/{CHAR_LIMITS.problemStatement}
                    </Typography>
                  </Box>
                </Box>

                {/* Revenue Model */}
                <Box className="mt-6 mb-6">
                  <Typography variant="subtitle1" className="text-white mb-2">
                    Revenue Model
                  </Typography>
                  <FormControl fullWidth size="small" className="bg-gray-800">
                    <Select
                      value={formData.monetization || ''}
                      onChange={(e) => handleInputChange('monetization', e.target.value)}
                      className="text-white"
                      label=""
                      aria-label="Revenue Model"
                    >
                      {MONETIZATION_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          className="flex items-center justify-between"
                        >
                          <span>{option.label}</span>
                          <Tooltip title={option.tooltip} arrow placement="right">
                            <IconButton
                              size="small"
                              className="text-secondary w-5 h-5 min-w-5 min-h-5 ml-3"
                            >
                              <HelpOutline sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Target Market */}
                <Box className="mb-6">
                  <Box className="flex items-center gap-1 mb-2">
                    <Typography variant="subtitle1" className="text-white">
                      Target Market
                    </Typography>
                    <Tooltip
                      title="Your target market is the specific group of customers most likely to buy your product or service. Think about demographics, behaviors, and pain points."
                      arrow
                      placement="right"
                    >
                      <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                        <HelpOutline sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={formData.targetAudience || ''}
                    onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                    placeholder="e.g., 'Small to medium restaurants (10-50 employees) in urban areas with $500K-$2M annual revenue'"
                    variant="outlined"
                    size="small"
                    className="bg-gray-800"
                    inputProps={{
                      maxLength: CHAR_LIMITS.targetAudience,
                    }}
                    InputProps={{
                      className: 'text-white',
                      style: { color: 'white' },
                    }}
                  />
                  <Box className="flex justify-end mt-1">
                    <Typography
                      variant="caption"
                      className={getCharCounterColor(
                        formData.targetAudience?.length || 0,
                        CHAR_LIMITS.targetAudience,
                      )}
                    >
                      {formData.targetAudience?.length || 0}/{CHAR_LIMITS.targetAudience}
                    </Typography>
                  </Box>
                </Box>

                {/* Competitive Advantage */}
                <Box className="mt-6 mb-6">
                  <Box className="flex items-center gap-1 mb-2">
                    <Typography variant="subtitle1" className="text-white">
                      Competitive Advantage
                    </Typography>
                    <Tooltip
                      title="What makes your solution unique or better than existing alternatives? Think about technology, approach, market positioning, or other differentiators."
                      arrow
                      placement="right"
                    >
                      <IconButton size="small" className="text-secondary w-5 h-5 min-w-5 min-h-5">
                        <HelpOutline sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    value={formData.differentiator || ''}
                    onChange={(e) => handleInputChange('differentiator', e.target.value)}
                    placeholder="e.g., 'AI-powered forecasting that's 30% more accurate than existing solutions'"
                    variant="outlined"
                    size="small"
                    multiline
                    rows={3}
                    className="bg-gray-800"
                    inputProps={{
                      maxLength: CHAR_LIMITS.differentiator,
                    }}
                    InputProps={{
                      className: 'text-white',
                      style: { color: 'white' },
                    }}
                  />
                  <Box className="flex justify-end mt-1">
                    <Typography
                      variant="caption"
                      className={getCharCounterColor(
                        formData.differentiator?.length || 0,
                        CHAR_LIMITS.differentiator,
                      )}
                    >
                      {formData.differentiator?.length || 0}/{CHAR_LIMITS.differentiator}
                    </Typography>
                  </Box>
                </Box>

                {/* Development Stage */}
                <Box className="mt-6 mb-6">
                  <Typography variant="subtitle1" className="text-white mb-2">
                    Current Stage
                  </Typography>
                  <FormControl fullWidth size="small" className="bg-gray-800">
                    <Select
                      value={formData.stage}
                      onChange={(e) => handleInputChange('stage', e.target.value)}
                      className="text-white"
                      label=""
                      aria-label="Current Stage"
                    >
                      {STAGE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          className="flex items-center justify-between"
                        >
                          <span>{option.label}</span>
                          <Tooltip title={option.tooltip} arrow placement="right">
                            <IconButton
                              size="small"
                              className="text-secondary w-5 h-5 min-w-5 min-h-5 ml-3"
                            >
                              <HelpOutline sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Continue Button */}
          <Box className="flex justify-center pt-8 pb-24">
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={!isDescriptionValid}
              className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-3 text-lg font-medium"
            >
              Continue
            </Button>
          </Box>
        </Box>
      </OnboardingStepWrapper>
    </Box>
  );
};

export default UserBusinessIdea;
